// Windows endpoint console (Windows PowerShell on Windows 11 / Server 2022).
//
// Intentionally NOT a Cisco device: IOS commands produce PowerShell's
// "term is not recognized" error. Supported tools are the ones a desk-side or
// NOC engineer uses on an endpoint: ipconfig, ping, tracert, nslookup, arp,
// route print, netstat, getmac, netsh, curl / Invoke-WebRequest and
// Test-NetConnection.

import {
  dev, hostIp, hostMediaUp, roundTrip, trace, dnsQuery, dhcpObtain, apipaFor, slaac, ping6, linkOf,
} from '../net/sim.js';
import { isIp, macWindows, maskToPrefix, networkOf, broadcastOf, isHostAddress, v6Expand } from '../net/addr.js';
import { remoteConnect, remoteLogin } from '../net/mgmtAccess.js';

const IOS_WORDS = new Set(['enable', 'en', 'configure', 'conf', 'show', 'sh', 'interface', 'int', 'switchport', 'no', 'end', 'hostname', 'router', 'vlan', 'copy', 'write', 'do', 'exit-if']);

// ------------------------------------------------ config helpers (used by GUI too)
export function setStatic(d, { ip, mask, gw, dns }) {
  d.ipcfg = { ...d.ipcfg, dhcp: false, ip, mask, gw: gw || null, dns: dns ?? d.ipcfg.dns };
  d.lease = null; d.apipa = null; d.arp = {};
}
export function setDhcp(net, d) {
  d.ipcfg = { ...d.ipcfg, dhcp: true, ip: null, mask: null, gw: null };
  renew(net, d);
}
export function renew(net, d) {
  const r = dhcpObtain(net, d.id);
  if (r.ok) { d.lease = r.lease; d.apipa = null; } else { d.lease = null; d.apipa = hostMediaUp(net, d) ? apipaFor(d.nic.mac) : null; }
  d.arp = {};
  return r;
}
/** Validate a static IPv4 config the way the Windows dialog does. Returns error string or null. */
export function validateStatic({ ip, mask, gw }) {
  if (!isIp(ip)) return `Invalid address parameter (${ip}). It should be a valid IPv4 address.`;
  if (!isIp(mask) || maskToPrefix(mask) === null || maskToPrefix(mask) === 0) return `Invalid mask parameter (${mask}).`;
  if (!isHostAddress(ip, mask)) return 'The combination of IP address and subnet mask is invalid. All of the bits in the host address portion of the IP address are set to 0 or 1.';
  if (gw && !isIp(gw)) return `Invalid gateway parameter (${gw}). It should be a valid IPv4 address.`;
  return null;
}

// ------------------------------------------------ tokenising with quotes
function argv(line) {
  const out = [];
  // Handles: plain, "quoted value", and key="quoted value" (netsh style).
  const re = /([\w-]+=)?"([^"]*)"|(\S+)/g;
  let m;
  while ((m = re.exec(line))) out.push(m[3] ?? `${m[1] || ''}${m[2]}`);
  return out;
}
const kv = (a) => { const m = a.match(/^(\w+)=(.*)$/); return m ? [m[1].toLowerCase(), m[2]] : null; };

export class WinShell {
  constructor(net, id, state) {
    this.net = net;
    this.id = id;
    this.state = state || { history: [], sub: null };
  }
  get d() { return dev(this.net, this.id); }
  bind(net) { this.net = net; }
  prompt() {
    if (this.state.sub === 'nslookup') return '> ';
    const r = this.state.remote;
    if (r?.ask === 'pass') return r.proto === 'ssh' ? `${r.user}@${r.ip}'s password: ` : 'Password: ';
    if (r?.ask === 'user') return 'Username: ';
    return `PS C:\\Users\\${this.d.user}> `;
  }
  isSecret() { return this.state.remote?.ask === 'pass'; }
  help() { return []; }
  complete() { return null; }

  exec(raw) {
    const line = raw.trim();
    const promptBefore = this.prompt();
    // A remote login waiting for a username / password (not stored in history).
    if (this.state.remote?.ask) return this.remoteAnswer(raw, promptBefore);
    if (!line) return this.out([], { key: '', empty: true, promptBefore });
    this.state.history.push(line);
    if (this.state.history.length > 50) this.state.history.shift();
    if (this.state.sub === 'nslookup') return this.nslookupInteractive(line, promptBefore);
    const a = argv(line);
    const cmd = a[0].toLowerCase().replace(/\.exe$/, '');
    const args = a.slice(1);
    const h = {
      ipconfig: () => this.ipconfig(args), ping: () => this.ping(args), tracert: () => this.tracert(args),
      nslookup: () => this.nslookup(args), arp: () => this.arp(args), route: () => this.route(args),
      netstat: () => this.netstat(args), getmac: () => this.getmac(), hostname: () => ({ lines: [this.d.hostname], key: 'hostname' }),
      cls: () => ({ lines: [], clear: true, key: 'cls' }), clear: () => ({ lines: [], clear: true, key: 'cls' }),
      netsh: () => this.netsh(args), 'test-netconnection': () => this.tnc(args), tnc: () => this.tnc(args),
      curl: () => (a[0].toLowerCase() === 'curl.exe' ? this.curlExe(args) : this.iwr(args)),
      'invoke-webrequest': () => this.iwr(args), iwr: () => this.iwr(args), help: () => this.labHelp(),
      whoami: () => ({ lines: [`${this.d.hostname.toLowerCase()}\\${this.d.user}`], key: 'whoami' }),
      'resolve-dnsname': () => this.resolveDnsName(args),
      'get-dnsserverzone': () => this.dnsZoneCmd(args),
      'get-dnsserverresourcerecord': () => this.dnsGet(args),
      'add-dnsserverresourcerecorda': () => this.dnsAdd(args),
      'remove-dnsserverresourcerecord': () => this.dnsRemove(args),
      ssh: () => this.ssh(args), telnet: () => this.telnet(args),
      'get-service': () => this.services('get', args), 'start-service': () => this.services('start', args),
      'stop-service': () => this.services('stop', args), 'restart-service': () => this.services('restart', args),
      'get-childitem': () => this.gci(args), gci: () => this.gci(args), dir: () => this.gci(args), ls: () => this.gci(args),
      'get-filehash': () => this.fileHash(args), 'get-content': () => this.gc(args), gc: () => this.gc(args), type: () => this.gc(args), cat: () => this.gc(args),
    }[cmd];
    if (!h) return this.out(this.notRecognized(a[0]), { key: 'unknown', ok: false, ios: IOS_WORDS.has(cmd), promptBefore });
    const r = h();
    return this.out(r.lines, { key: r.key || cmd, args: r.args || {}, result: r.result || {}, ok: r.ok !== false, clear: r.clear, promptBefore });
  }

  out(lines, ev) {
    const L = lines.map((t) => (typeof t === 'string' ? { text: t } : t));
    return { lines: L, clear: ev.clear, event: { device: this.id, line: this.state.history.at(-1) || '', output: L.map((x) => x.text).join('\n'), ...ev } };
  }

  notRecognized(w) {
    return [
      { text: `${w} : The term '${w}' is not recognized as the name of a cmdlet, function, script file, or operable`, cls: 'err' },
      { text: 'program. Check the spelling of the name, or if a path was included, verify that the path is correct and', cls: 'err' },
      { text: 'try again.', cls: 'err' },
      { text: 'At line:1 char:1', cls: 'err' },
      { text: `+ ${w}`, cls: 'err' },
      { text: `+ ${'~'.repeat(w.length)}`, cls: 'err' },
      { text: `    + CategoryInfo          : ObjectNotFound: (${w}:String) [], CommandNotFoundException`, cls: 'err' },
      { text: '    + FullyQualifiedErrorId : CommandNotFoundException', cls: 'err' },
      '',
    ];
  }

  labHelp() {
    return {
      key: 'help',
      lines: [
        '[Lab] Endpoint troubleshooting tools available on this Windows host:',
        '  ipconfig [/all | /release | /renew | /flushdns]',
        '  ping [-n count] [-6] <host>          tracert [-d] <host>',
        '  nslookup [name] [server]             arp -a | arp -d *',
        '  route print                          netstat [-ano]',
        '  getmac                               hostname',
        '  curl <url>  (Invoke-WebRequest)      curl.exe -I <url>',
        '  Test-NetConnection <host> [-Port n]',
        '  ssh -l <user> <host>  |  ssh <user>@<host>      telnet <host>',
        '  netsh interface ip set address name="Ethernet" static <ip> <mask> <gw>',
        '  netsh interface ip set address name="Ethernet" dhcp',
        '  netsh interface ip set dns name="Ethernet" static <dns-ip>',
        '  netsh interface set interface "Ethernet" enable|disable',
        '  netsh interface ip show config',
        '  Resolve-DnsName <name> [-Server <ip>]',
        ...(this.d.dnsRecords ? ['  Get-DnsServerZone | Get-DnsServerResourceRecord -ZoneName <zone> [-Name <n>]', '  Add-DnsServerResourceRecordA -ZoneName <zone> -Name <n> -IPv4Address <ip>', '  Remove-DnsServerResourceRecord -ZoneName <zone> -Name <n> -RRType A -Force'] : []),
        '',
      ],
    };
  }

  // ------------------------------------------------ ipconfig
  ipconfig(args) {
    const d = this.d;
    const sw = (args[0] || '').toLowerCase();
    if (sw === '/release') {
      if (!d.ipcfg.dhcp) return { key: 'ipconfig /release', lines: ['', 'Windows IP Configuration', '', 'The operation failed as no adapter is in the state permissible for', 'this operation.', ''], ok: false };
      d.lease = null; d.apipa = null; d.arp = {};
      return { key: 'ipconfig /release', lines: ['', 'Windows IP Configuration', '', ...this.adapterBlock(false)] };
    }
    if (sw === '/renew') {
      if (!d.ipcfg.dhcp) return { key: 'ipconfig /renew', lines: ['', 'Windows IP Configuration', '', 'The operation failed as no adapter is in the state permissible for', 'this operation.', ''], ok: false };
      if (!hostMediaUp(this.net, d)) return { key: 'ipconfig /renew', lines: ['', 'Windows IP Configuration', '', 'No operation can be performed on Ethernet while it has its media disconnected.', ''], ok: false };
      const r = renew(this.net, d);
      const lines = ['', 'Windows IP Configuration', ''];
      if (!r.ok) lines.push({ text: 'An error occurred while renewing interface Ethernet : unable to contact your DHCP server. Request has timed out.', delay: 2500 }, '');
      else lines.push({ text: '', delay: 1200 });
      return { key: 'ipconfig /renew', lines: [...lines, ...this.adapterBlock(false)], result: { success: r.ok } };
    }
    if (sw === '/flushdns') return { key: 'ipconfig /flushdns', lines: ['', 'Windows IP Configuration', '', 'Successfully flushed the DNS Resolver Cache.'] };
    const all = sw === '/all';
    if (sw && !all) return { key: 'ipconfig', lines: ['', 'Error: unrecognized or incomplete command line.', '', 'USAGE:', '    ipconfig [/allcompartments] [/? | /all | /renew [adapter] | /release [adapter] | /flushdns ]'], ok: false };
    const head = ['', 'Windows IP Configuration', ''];
    if (all) {
      head.push(
        `   Host Name . . . . . . . . . . . . : ${d.hostname}`,
        '   Primary Dns Suffix  . . . . . . . : ',
        '   Node Type . . . . . . . . . . . . : Hybrid',
        '   IP Routing Enabled. . . . . . . . : No',
        '   WINS Proxy Enabled. . . . . . . . : No',
        '',
      );
    }
    return { key: all ? 'ipconfig /all' : 'ipconfig', lines: [...head, ...this.adapterBlock(all)] };
  }

  adapterBlock(all) {
    const d = this.d;
    if (!d.nic.adminUp) return []; // disabled adapters are not listed at all
    const out = ['Ethernet adapter Ethernet:', ''];
    const mac = macWindows(d.nic.mac);
    if (!hostMediaUp(this.net, d)) {
      out.push('   Media State . . . . . . . . . . . : Media disconnected', '   Connection-specific DNS Suffix  . : ');
      if (all) out.push('   Description . . . . . . . . . . . : Intel(R) Ethernet Connection (7) I219-V', `   Physical Address. . . . . . . . . : ${mac}`, `   DHCP Enabled. . . . . . . . . . . : ${d.ipcfg.dhcp ? 'Yes' : 'No'}`, '   Autoconfiguration Enabled . . . . : Yes');
      return [...out, ''];
    }
    const c = hostIp(d);
    const v6 = slaac(this.net, d.id);
    out.push(`   Connection-specific DNS Suffix  . : ${c?.domain || d.ipcfg?.dnsSuffix || ''}`);
    if (all) out.push('   Description . . . . . . . . . . . : Intel(R) Ethernet Connection (7) I219-V', `   Physical Address. . . . . . . . . : ${mac}`, `   DHCP Enabled. . . . . . . . . . . : ${d.ipcfg.dhcp ? 'Yes' : 'No'}`, '   Autoconfiguration Enabled . . . . : Yes');
    for (const a of v6.addrs.filter((x) => x.type === 'global')) out.push(`   IPv6 Address. . . . . . . . . . . : ${a.addr}${all ? '(Preferred)' : ''}`);
    out.push(`   Link-local IPv6 Address . . . . . : ${v6.addrs.find((x) => x.type === 'link-local').addr}%12${all ? '(Preferred)' : ''}`);
    if (c?.apipa) out.push(`   Autoconfiguration IPv4 Address. . : ${c.ip}${all ? '(Preferred)' : ''}`);
    else if (c) out.push(`   IPv4 Address. . . . . . . . . . . : ${c.ip}${all ? '(Preferred)' : ''}`);
    if (c) out.push(`   Subnet Mask . . . . . . . . . . . : ${c.mask}`);
    if (all && d.lease) out.push('   Lease Obtained. . . . . . . . . . : Wednesday, September 30, 2026 9:02:11 AM', '   Lease Expires . . . . . . . . . . : Thursday, October 1, 2026 9:02:11 AM');
    const gws = [v6.gw ? `${v6.gw}%12` : null, c?.gw].filter(Boolean);
    out.push(`   Default Gateway . . . . . . . . . : ${gws[0] || ''}`);
    for (const g of gws.slice(1)) out.push(`                                       ${g}`);
    if (all) {
      if (d.lease) out.push(`   DHCP Server . . . . . . . . . . . : ${d.lease.server}`);
      const dns = c?.dns || [];
      out.push(`   DNS Servers . . . . . . . . . . . : ${dns[0] || ''}`);
      for (const s of dns.slice(1)) out.push(`                                       ${s}`);
      out.push('   NetBIOS over Tcpip. . . . . . . . : Enabled');
    }
    return [...out, ''];
  }

  // ------------------------------------------------ ping
  ping(args) {
    let n = 4, v6 = false, target = null;
    for (let i = 0; i < args.length; i++) {
      const a = args[i].toLowerCase();
      if (a === '-n' || a === '/n') n = Math.max(1, Math.min(10, +args[++i] || 4));
      else if (a === '-t' || a === '/t') n = 6;
      else if (a === '-6') v6 = true;
      else if (a === '-4') v6 = false;
      else if (a === '-l' || a === '-w' || a === '-i') i++;
      else target = args[i];
    }
    if (!target) return { key: 'ping', ok: false, lines: ['', 'Usage: ping [-t] [-a] [-n count] [-l size] [-f] [-i TTL] [-v TOS]', '            [-r count] [-s count] [[-j host-list] | [-k host-list]]', '            [-w timeout] [-R] [-S srcaddr] [-c compartment] [-p]', '            [-4] [-6] target_name', ''] };
    if (v6 || (target.includes(':') && v6Expand(target))) return this.pingV6(target, n);
    let ip = target, label = target;
    if (!isIp(target)) {
      if (/^[\d.]+$/.test(target)) return { key: 'ping', ok: false, args: { target }, lines: [`Ping request could not find host ${target}. Please check the name and try again.`] };
      const q = hostMediaUp(this.net, this.d) ? dnsQuery(this.net, this.id, target) : { ok: false };
      if (!q.ok) {
        return { key: 'ping', ok: false, args: { target }, result: { success: false, dnsFailed: true }, lines: [{ text: `Ping request could not find host ${target}. Please check the name and try again.`, delay: q.reason === 'timeout' ? 2000 : 100 }] };
      }
      ip = q.address; label = `${target} [${ip}]`;
    }
    const out = ['', `Pinging ${label} with 32 bytes of data:`];
    let echo = 0, unreach = 0, fail = 0;
    const times = [];
    const own = hostIp(this.d)?.ip;
    for (let k = 0; k < n; k++) {
      if (ip.startsWith('127.') || ip === own) {
        if (ip === own && !hostMediaUp(this.net, this.d)) { out.push({ text: 'PING: transmit failed. General failure. ', delay: 400 }); fail++; continue; }
        out.push({ text: `Reply from ${ip}: bytes=32 time<1ms TTL=128`, delay: k ? 1000 : 50 }); echo++; times.push(0); continue;
      }
      const rt = roundTrip(this.net, this.id, ip, { ttl: 128 });
      if (rt.ok) {
        if (rt.lossy && k % 2) { out.push({ text: 'Request timed out.', delay: 4000 }); continue; }
        const t = rt.hops <= 1 ? 0 : rt.hops * 4 + 3 + (k % 3);
        out.push({ text: `Reply from ${ip}: bytes=32 time${t ? `=${t}ms` : '<1ms'} TTL=${rt.replyTtl}`, delay: k ? 1000 : 60 });
        echo++; times.push(t);
      } else if (rt.stage === 'no-source') {
        out.push({ text: 'PING: transmit failed. General failure. ', delay: 400 }); fail++;
      } else if (rt.stage === 'forward' && rt.fwd.at === this.id && rt.fwd.reason === 'arp') {
        out.push({ text: `Reply from ${own}: Destination host unreachable.`, delay: 3000 }); unreach++;
      } else if (rt.stage === 'forward' && rt.fwd.at === this.id && rt.fwd.reason === 'no-route') {
        out.push({ text: 'PING: transmit failed. General failure. ', delay: 400 }); fail++;
      } else if (rt.unreachableFrom) {
        out.push({ text: `Reply from ${rt.unreachableFrom}: Destination net unreachable.`, delay: 1000 }); unreach++;
      } else {
        out.push({ text: 'Request timed out.', delay: 4000 });
      }
    }
    const recv = echo + unreach; // Windows counts "unreachable" replies as received!
    out.push('', `Ping statistics for ${ip}:`, `    Packets: Sent = ${n}, Received = ${fail ? 0 : recv}, Lost = ${fail ? n : n - recv} (${Math.round(((fail ? n : n - recv) / n) * 100)}% loss),`);
    if (echo) {
      out.push('Approximate round trip times in milli-seconds:', `    Minimum = ${Math.min(...times)}ms, Maximum = ${Math.max(...times)}ms, Average = ${Math.round(times.reduce((a, b) => a + b, 0) / times.length)}ms`);
    }
    return { key: 'ping', args: { target, ip }, result: { success: echo > 0, echo, unreach, sent: n, allOk: echo === n }, lines: out };
  }

  pingV6(target, n) {
    const ok = ping6(this.net, this.id, target);
    const out = ['', `Pinging ${target} with 32 bytes of data:`];
    for (let k = 0; k < n; k++) out.push(ok ? { text: `Reply from ${target}: time<1ms`, delay: 500 } : { text: hostMediaUp(this.net, this.d) ? 'Destination host unreachable.' : 'PING: transmit failed. General failure. ', delay: 1500 });
    out.push('', `Ping statistics for ${target}:`, `    Packets: Sent = ${n}, Received = ${ok ? n : 0}, Lost = ${ok ? 0 : n} (${ok ? 0 : 100}% loss),`);
    if (ok) out.push('Approximate round trip times in milli-seconds:', '    Minimum = 0ms, Maximum = 0ms, Average = 0ms');
    return { key: 'ping', args: { target, ip: target, v6: true }, result: { success: ok, allOk: ok }, lines: out };
  }

  // ------------------------------------------------ tracert
  tracert(args) {
    const target = args.filter((a) => !a.startsWith('-') && !/^\d+$/.test(a) || isIp(a)).pop();
    if (!target) return { key: 'tracert', ok: false, lines: ['', 'Usage: tracert [-d] [-h maximum_hops] [-j host-list] [-w timeout]', '               [-R] [-S srcaddr] [-4] [-6] target_name', ''] };
    let ip = target, label = target;
    if (!isIp(target)) {
      const q = dnsQuery(this.net, this.id, target);
      if (!q.ok) return { key: 'tracert', args: { target }, ok: false, lines: [{ text: `Unable to resolve target system name ${target}.`, delay: 1500 }] };
      ip = q.address; label = `${target} [${ip}]`;
    }
    if (!hostIp(this.d) || !hostMediaUp(this.net, this.d)) return { key: 'tracert', args: { target, ip }, lines: ['', `Tracing route to ${label}`, 'over a maximum of 30 hops', '', 'Transmit error: code 1231.', '', 'Trace complete.'] };
    const hops = trace(this.net, this.id, ip, 30);
    const out = ['', `Tracing route to ${label}`, 'over a maximum of 30 hops:', ''];
    let reached = false;
    hops.forEach((h, k) => {
      const n = String(k + 1).padStart(3);
      if (h.unreachable) out.push({ text: `${n}  ${h.ip}  reports: Destination net unreachable.`, delay: 800 });
      else if (h.ip) {
        const t = k === 0 ? '   <1 ms' : `${String(k * 6 - 3).padStart(5)} ms`;
        out.push({ text: `${n}${t}${t}${t}  ${h.ip}`, delay: 600 });
        if (h.final) reached = true;
      } else out.push({ text: `${n}     *        *        *     Request timed out.`, delay: 1500 });
    });
    out.push('', 'Trace complete.');
    return { key: 'tracert', args: { target, ip }, result: { success: reached, hops }, lines: out };
  }

  // ------------------------------------------------ nslookup
  nslookup(args) {
    const name = args.find((a) => !a.startsWith('-'));
    const server = args.filter((a) => !a.startsWith('-'))[1];
    if (!name) {
      this.state.sub = 'nslookup';
      const s = hostIp(this.d)?.dns?.[0];
      return { key: 'nslookup', lines: s ? [`Default Server:  ${this.serverName(s)}`, `Address:  ${s}`, ''] : ['*** Default servers are not available', 'Default Server:  UnKnown', 'Address:  127.0.0.1', ''] };
    }
    return this.resolveOut(name, server);
  }
  nslookupInteractive(line, promptBefore) {
    if (line.toLowerCase() === 'exit') { this.state.sub = null; return this.out([], { key: 'nslookup exit', promptBefore }); }
    const r = this.resolveOut(line.split(/\s+/)[0]);
    return this.out(r.lines.slice(3), { key: 'nslookup', args: r.args, result: r.result, promptBefore });
  }
  serverName(ip) {
    const d = Object.values(this.net.devices).find((x) => x.ipcfg?.ip === ip && x.dnsName);
    return d?.dnsName || 'UnKnown';
  }
  resolveOut(name, server) {
    const media = hostMediaUp(this.net, this.d);
    const q = media ? dnsQuery(this.net, this.id, name, server) : { ok: false, reason: 'timeout', server: server || hostIp(this.d)?.dns?.[0] };
    const srv = q.server || server;
    if (q.reason === 'no-server') {
      return { key: 'nslookup', args: { name }, result: { success: false }, lines: ['*** Default servers are not available', 'Server:  UnKnown', 'Address:  127.0.0.1', '', `*** UnKnown can't find ${name}: No response from server`] };
    }
    if (!q.ok && q.reason !== 'nxdomain') {
      return {
        key: 'nslookup', args: { name, server: srv }, result: { success: false, timeout: true },
        lines: [
          { text: 'DNS request timed out.', delay: 2000 }, '    timeout was 2 seconds.', 'Server:  UnKnown', `Address:  ${srv}`, '',
          { text: 'DNS request timed out.', delay: 2000 }, '    timeout was 2 seconds.',
          { text: 'DNS request timed out.', delay: 2000 }, '    timeout was 2 seconds.',
          `*** Request to UnKnown timed-out`,
        ],
      };
    }
    const head = [`Server:  ${q.serverName}`, `Address:  ${srv}`, ''];
    if (!q.ok) return { key: 'nslookup', args: { name, server: srv }, result: { success: false, nx: true }, lines: [...head, `*** ${q.serverName} can't find ${name}: Non-existent domain`] };
    return {
      key: 'nslookup', args: { name, server: srv }, result: { success: true, address: q.address },
      lines: [...head, ...(q.authoritative ? [] : ['Non-authoritative answer:']), `Name:    ${name}`, `Address:  ${q.address}`, ''],
    };
  }

  // ------------------------------------------------ PowerShell DNS (client + DNS Server role)
  psArgs(args) {
    const o = { _: [] };
    for (let i = 0; i < args.length; i++) {
      if (/^-\w/.test(args[i])) { const k = args[i].slice(1).toLowerCase(); const v = args[i + 1]; if (v === undefined || /^-\w/.test(v)) o[k] = true; else { o[k] = v; i++; } } else o._.push(args[i]);
    }
    return o;
  }
  resolveDnsName(args) {
    const a = this.psArgs(args);
    const name = a.name || a._[0];
    if (!name) return { key: 'Resolve-DnsName', ok: false, lines: this.psErr('Resolve-DnsName', "Cannot process command because of one or more missing mandatory parameters: Name.", 'InvalidArgument: (:) [Resolve-DnsName], ParameterBindingException') };
    const q = hostMediaUp(this.net, this.d) ? dnsQuery(this.net, this.id, name, isIp(a.server) ? a.server : undefined) : { ok: false, reason: 'timeout' };
    if (!q.ok) {
      const msg = q.reason === 'nxdomain' ? `${name} : DNS name does not exist` : `${name} : This operation returned because the timeout period expired`;
      return { key: 'Resolve-DnsName', args: { name, server: q.server }, result: { success: false, nx: q.reason === 'nxdomain', timeout: q.reason !== 'nxdomain' }, lines: [{ text: '', delay: q.reason === 'nxdomain' ? 100 : 2000 }, ...this.psErr('Resolve-DnsName', msg, `ResourceUnavailable: (${name}:String) [Resolve-DnsName], Win32Exception`)] };
    }
    const fq = name.includes('.') ? name : `${name}.${hostIp(this.d)?.domain || this.d.ipcfg?.dnsSuffix || ''}`.replace(/\.$/, '');
    return { key: 'Resolve-DnsName', args: { name, server: q.server }, result: { success: true, address: q.address }, lines: ['', 'Name                                           Type   TTL   Section    IPAddress', '----                                           ----   ---   -------    ---------', `${fq.padEnd(47)}A      3600  Answer     ${q.address}`, ''] };
  }
  psErr(cmd, msg, cat) {
    return [{ text: `${cmd} : ${msg}`, cls: 'err' }, { text: 'At line:1 char:1', cls: 'err' }, { text: `+ ${this.state.history.at(-1)}`, cls: 'err' }, { text: `+ ${'~'.repeat(Math.min(60, this.state.history.at(-1).length))}`, cls: 'err' }, { text: `    + CategoryInfo          : ${cat}`, cls: 'err' }, ''];
  }
  dnsRole(cmd) {
    if (this.d.dnsRecords && this.d.dnsZone) return null;
    return { key: cmd, ok: false, lines: this.notRecognized(cmd) };
  }
  zoneOk(cmd, zone) {
    if (zone && zone.toLowerCase() === this.d.dnsZone) return null;
    return { key: cmd, ok: false, lines: this.psErr(cmd, `Failed to get the zone information for ${zone || '(none)'} on server ${this.d.hostname}.`, `ObjectNotFound: (${zone || ''}:root/Microsoft/...DnsServerZone) [${cmd}], CimException`) };
  }
  recordsOf() {
    const z = this.d.dnsZone;
    return Object.entries(this.d.dnsRecords).filter(([n]) => n === z || n.endsWith(`.${z}`)).map(([n, ip]) => ({ host: n === z ? '@' : n.slice(0, -z.length - 1), fqdn: n, ip }));
  }
  dnsZoneCmd() {
    const e = this.dnsRole('Get-DnsServerZone'); if (e) return e;
    return { key: 'Get-DnsServerZone', lines: ['', 'ZoneName                            ZoneType        IsAutoCreated   IsDsIntegrated  IsReverseLookupZone  IsSigned', '--------                            --------        -------------   --------------  -------------------  --------', `${this.d.dnsZone.padEnd(36)}Primary         False           False           False                False`, ''] };
  }
  dnsGet(args) {
    const cmd = 'Get-DnsServerResourceRecord';
    const e = this.dnsRole(cmd); if (e) return e;
    const a = this.psArgs(args);
    const z = this.zoneOk(cmd, a.zonename); if (z) return z;
    let recs = this.recordsOf();
    if (a.name) {
      recs = recs.filter((r) => r.host.toLowerCase() === String(a.name).toLowerCase());
      if (!recs.length) return { key: cmd, ok: false, args: { zone: a.zonename, name: a.name }, lines: this.psErr(cmd, `Failed to get ${a.name} record in ${this.d.dnsZone} zone on ${this.d.hostname} server.`, `ObjectNotFound: (${a.name}:root/Microsoft/...rverResourceRecord) [${cmd}], CimException`) };
    }
    const own = hostIp(this.d)?.ip;
    const rows = [`${'@'.padEnd(26)}NS         2     0           01:00:00        ${this.d.hostname.toLowerCase()}.${this.d.dnsZone}.`, `${'@'.padEnd(26)}SOA        6     0           01:00:00        [3][${this.d.hostname.toLowerCase()}.${this.d.dnsZone}.]`];
    if (a.name) rows.length = 0;
    for (const r of recs.sort((x, y) => (x.host > y.host ? 1 : -1))) rows.push(`${r.host.padEnd(26)}A          1     0           01:00:00        ${r.ip}`);
    return { key: cmd, args: { zone: a.zonename, name: a.name || null }, result: { records: recs.map((r) => `${r.fqdn}=${r.ip}`), server: own }, lines: ['', 'HostName                  RecordType Type  Timestamp   TimeToLive      RecordData', '--------                  ---------- ----  ---------   ----------      ----------', ...rows, ''] };
  }
  dnsAdd(args) {
    const cmd = 'Add-DnsServerResourceRecordA';
    const e = this.dnsRole(cmd); if (e) return e;
    const a = this.psArgs(args);
    const z = this.zoneOk(cmd, a.zonename); if (z) return z;
    const ip = a.ipv4address;
    if (!a.name || a.name === true || !isIp(ip)) return { key: cmd, ok: false, lines: this.psErr(cmd, 'Cannot validate argument on parameter. Supply -Name <host> and -IPv4Address <a.b.c.d>.', `InvalidData: (:) [${cmd}], ParameterBindingValidationException`) };
    const fqdn = `${String(a.name).toLowerCase()}.${this.d.dnsZone}`;
    if (this.d.dnsRecords[fqdn]) return { key: cmd, ok: false, args: { name: a.name, ip }, lines: this.psErr(cmd, `Failed to create resource record ${a.name} in ${this.d.dnsZone} zone on server ${this.d.hostname}: a record with this name already exists.`, `ResourceExists: (${a.name}:root/Microsoft/...rverResourceRecord) [${cmd}], CimException`) };
    this.d.dnsRecords[fqdn] = ip;
    return { key: cmd, args: { name: String(a.name).toLowerCase(), ip, fqdn }, lines: [''] };
  }
  dnsRemove(args) {
    const cmd = 'Remove-DnsServerResourceRecord';
    const e = this.dnsRole(cmd); if (e) return e;
    const a = this.psArgs(args);
    const z = this.zoneOk(cmd, a.zonename); if (z) return z;
    const fqdn = `${String(a.name || '').toLowerCase()}.${this.d.dnsZone}`;
    if (!a.name || String(a.rrtype || '').toUpperCase() !== 'A') return { key: cmd, ok: false, lines: this.psErr(cmd, 'Supply -Name <host> -RRType A.', `InvalidArgument: (:) [${cmd}], ParameterBindingException`) };
    if (!this.d.dnsRecords[fqdn]) return { key: cmd, ok: false, lines: this.psErr(cmd, `Failed to get ${a.name} record in ${this.d.dnsZone} zone on ${this.d.hostname} server.`, `ObjectNotFound: (${a.name}:root/Microsoft/...rverResourceRecord) [${cmd}], CimException`) };
    if (!a.force) return { key: cmd, ok: false, lines: ['', 'Confirm', `Are you sure you want to remove ${a.name} A record from ${this.d.dnsZone}?`, '[Lab] This console cannot answer interactive prompts — repeat the command with -Force.', ''] };
    delete this.d.dnsRecords[fqdn];
    return { key: cmd, args: { name: String(a.name).toLowerCase(), fqdn }, lines: [''] };
  }

  // ------------------------------------------------ arp / route / netstat / getmac
  arp(args) {
    const a = (args[0] || '').toLowerCase();
    const d = this.d;
    if (a === '-d') { d.arp = {}; return { key: 'arp -d', lines: [] }; }
    if (a !== '-a' && a !== '-g') return { key: 'arp', ok: false, lines: ['', 'Displays and modifies the IP-to-Physical address translation tables used by', 'address resolution protocol (ARP).', '', 'ARP -s inet_addr eth_addr [if_addr]', 'ARP -d inet_addr [if_addr]', 'ARP -a [inet_addr] [-N if_addr] [-v]', ''] };
    const c = hostIp(d);
    if (!c || !hostMediaUp(this.net, d)) return { key: 'arp -a', lines: ['No ARP Entries Found.'] };
    const rows = Object.entries(d.arp).map(([ip, e]) => `  ${ip.padEnd(22)}${macWindows(e.mac).toLowerCase().padEnd(22)}dynamic`);
    rows.push(`  ${broadcastOf(c.ip, c.mask).padEnd(22)}${'ff-ff-ff-ff-ff-ff'.padEnd(22)}static`);
    rows.push(`  ${'224.0.0.22'.padEnd(22)}${'01-00-5e-00-00-16'.padEnd(22)}static`, `  ${'224.0.0.251'.padEnd(22)}${'01-00-5e-00-00-fb'.padEnd(22)}static`, `  ${'239.255.255.250'.padEnd(22)}${'01-00-5e-7f-ff-fa'.padEnd(22)}static`);
    return { key: 'arp -a', result: { entries: Object.keys(d.arp) }, lines: ['', `Interface: ${c.ip} --- 0xc`, '  Internet Address      Physical Address      Type', ...rows, ''] };
  }

  route(args) {
    if ((args[0] || '').toLowerCase() !== 'print') return { key: 'route', ok: false, lines: ['', 'Manipulates network routing tables.', '', 'ROUTE [-f] [-p] [-4|-6] command [destination]', '                  [MASK netmask]  [gateway] [METRIC metric]  [IF interface]', ''] };
    const d = this.d;
    const c = hostMediaUp(this.net, d) ? hostIp(d) : null;
    const macs = macWindows(d.nic.mac).replace(/-/g, ' ').toLowerCase();
    const r = (a, b, g, i, m) => `${a.padStart(17)}${b.padStart(17)}${g.padStart(17)}${i.padStart(17)}${String(m).padStart(7)}`;
    const out = ['===========================================================================', 'Interface List',
      ` 12...${macs} ......Intel(R) Ethernet Connection (7) I219-V`, '  1...........................Software Loopback Interface 1',
      '===========================================================================', '', 'IPv4 Route Table',
      '===========================================================================', 'Active Routes:',
      'Network Destination        Netmask          Gateway       Interface  Metric'];
    if (c?.gw) out.push(r('0.0.0.0', '0.0.0.0', c.gw, c.ip, 25));
    out.push(r('127.0.0.0', '255.0.0.0', 'On-link', '127.0.0.1', 331), r('127.0.0.1', '255.255.255.255', 'On-link', '127.0.0.1', 331), r('127.255.255.255', '255.255.255.255', 'On-link', '127.0.0.1', 331));
    if (c) out.push(r(networkOf(c.ip, c.mask), c.mask, 'On-link', c.ip, 281), r(c.ip, '255.255.255.255', 'On-link', c.ip, 281), r(broadcastOf(c.ip, c.mask), '255.255.255.255', 'On-link', c.ip, 281));
    out.push(r('224.0.0.0', '240.0.0.0', 'On-link', '127.0.0.1', 331));
    if (c) out.push(r('224.0.0.0', '240.0.0.0', 'On-link', c.ip, 281));
    out.push(r('255.255.255.255', '255.255.255.255', 'On-link', '127.0.0.1', 331));
    if (c) out.push(r('255.255.255.255', '255.255.255.255', 'On-link', c.ip, 281));
    out.push('===========================================================================', 'Persistent Routes:');
    if (!d.ipcfg.dhcp && d.ipcfg.gw) out.push('  Network Address          Netmask  Gateway Address  Metric', `${'0.0.0.0'.padStart(17)}${'0.0.0.0'.padStart(17)}${d.ipcfg.gw.padStart(17)}  Default`);
    else out.push('  None');
    out.push('===========================================================================', '');
    return { key: 'route print', lines: out };
  }

  netstat(args) {
    const d = this.d;
    const flags = args.join('').toLowerCase();
    if (flags.includes('r')) return { ...this.route(['print']), key: 'netstat -r' };
    const c = hostIp(d);
    const L = (p, l, f, s, pid) => `  ${p.padEnd(7)}${l.padEnd(23)}${f.padEnd(23)}${s.padEnd(16)}${flags.includes('o') ? pid : ''}`;
    const out = ['', 'Active Connections', '', `  Proto  Local Address          Foreign Address        State${flags.includes('o') ? '           PID' : ''}`];
    const listen = [
      ...(flags.includes('a') ? [['TCP', '0.0.0.0:135', 1032], ['TCP', '0.0.0.0:445', 4], ['TCP', '0.0.0.0:5040', 6512]] : []),
      ...d.services.filter((s) => flags.includes('a') || false).map((s) => [s.proto.toUpperCase(), `0.0.0.0:${s.port}`, s.pid || 2760]),
    ];
    for (const [p, l, pid] of listen) out.push(p === 'UDP' ? L('UDP', l, '*:*', '', pid) : L('TCP', l, '0.0.0.0:0', 'LISTENING', pid));
    if (c && flags.includes('a')) out.push(L('TCP', `${c.ip}:139`, '0.0.0.0:0', 'LISTENING', 4));
    for (const s of d.connections.slice(-6)) out.push(L('TCP', `${c?.ip || '0.0.0.0'}:${s.lport}`, `${s.ip}:${s.port}`, s.state, s.pid || 8120));
    if (flags.includes('a')) out.push(L('UDP', '0.0.0.0:5353', '*:*', '', 2144), L('UDP', '0.0.0.0:5355', '*:*', '', 2144));
    out.push('');
    return { key: flags.includes('a') ? 'netstat -ano' : 'netstat', lines: out, result: { listening: d.services.map((s) => `${s.proto}/${s.port}`) } };
  }

  getmac() {
    return {
      key: 'getmac', result: { mac: this.d.nic.mac },
      lines: ['', 'Physical Address    Transport Name', '=================== ==========================================================',
        `${macWindows(this.d.nic.mac)}   ${this.d.nic.adminUp && hostMediaUp(this.net, this.d) ? '\\Device\\Tcpip_{4D36E972-E325-11CE-BFC1-08002BE10318}' : 'Media disconnected'}`, ''],
    };
  }

  // ------------------------------------------------ netsh
  netsh(args) {
    const low = args.map((a) => a.toLowerCase());
    const bad = { key: 'netsh', ok: false, lines: ['The syntax supplied for this command is not valid. Check help for the correct syntax.', ''] };
    const d = this.d;
    if (low[0] !== 'interface' && low[0] !== 'int') return bad;
    // netsh interface set interface "Ethernet" enable|disable  (also admin=enabled)
    if (low[1] === 'set' && low[2] === 'interface') {
      const name = kv(args[3])?.[1] ?? args[3];
      const st = (kv(args[4])?.[1] ?? args[4] ?? '').toLowerCase();
      if (!name || name.toLowerCase() !== 'ethernet') return { key: 'netsh set interface', ok: false, lines: ['The interface name is not registered with the router.', ''] };
      if (/^(enable|enabled)$/.test(st)) d.nic.adminUp = true;
      else if (/^(disable|disabled)$/.test(st)) d.nic.adminUp = false;
      else return bad;
      if (d.ipcfg.dhcp && d.nic.adminUp) renew(this.net, d);
      return { key: 'netsh set interface', args: { state: st }, lines: [''] };
    }
    if (!['ip', 'ipv4'].includes(low[1])) return bad;
    if (low[2] === 'show' && (low[3] === 'config' || low[3] === 'addresses')) return this.netshShow();
    if (low[2] !== 'set') return bad;
    const what = low[3];
    const rest = args.slice(4).map((a) => kv(a) || [null, a]);
    const name = rest[0]?.[1];
    if (!name || name.toLowerCase() !== 'ethernet') return { key: 'netsh set', ok: false, lines: ['The filename, directory name, or volume label syntax is incorrect.', ''] };
    const vals = rest.slice(1).filter(([k]) => !k || ['source', 'address', 'mask', 'gateway', 'addr'].includes(k)).map(([, v]) => v);
    const named = Object.fromEntries(rest.filter(([k]) => k));
    const src = (named.source || vals[0] || '').toLowerCase();
    if (what === 'address') {
      if (src === 'dhcp') { setDhcp(this.net, d); return { key: 'netsh set address', args: { dhcp: true }, lines: [''] }; }
      if (src !== 'static') return bad;
      const ip = named.address || vals[1], mask = named.mask || vals[2], gw = named.gateway || vals[3];
      if (!ip || !mask) return bad;
      const err = validateStatic({ ip, mask, gw });
      if (err) return { key: 'netsh set address', ok: false, lines: [err, ''] };
      setStatic(d, { ip, mask, gw });
      return { key: 'netsh set address', args: { ip, mask, gw }, lines: [''] };
    }
    if (what === 'dns' || what === 'dnsservers') {
      if (src === 'dhcp') { d.ipcfg.dns = []; return { key: 'netsh set dns', lines: [''] }; }
      const ip = named.address || vals[1];
      if (src !== 'static' || !isIp(ip)) return { key: 'netsh set dns', ok: false, lines: [`The DNS server address ${ip || ''} is not valid.`, ''] };
      d.ipcfg.dns = [ip];
      return { key: 'netsh set dns', args: { dns: ip }, lines: [''] };
    }
    return bad;
  }
  netshShow() {
    const d = this.d;
    const c = hostIp(d);
    return {
      key: 'netsh show config',
      lines: ['', 'Configuration for interface "Ethernet"', `    DHCP enabled:                         ${d.ipcfg.dhcp ? 'Yes' : 'No'}`,
        ...(c ? [`    IP Address:                           ${c.ip}`, `    Subnet Prefix:                        ${networkOf(c.ip, c.mask)}/${maskToPrefix(c.mask)} (mask ${c.mask})`] : []),
        ...(c?.gw ? [`    Default Gateway:                      ${c.gw}`, '    Gateway Metric:                       0'] : []),
        '    InterfaceMetric:                      25',
        `    ${d.ipcfg.dhcp ? 'DNS servers configured through DHCP' : 'Statically Configured DNS Servers'}:  ${(c?.dns || [])[0] || 'None'}`,
        '    Register with which suffix:           Primary only', ''],
    };
  }

  // ------------------------------------------------ application tests
  connect(host, port) {
    let ip = host;
    if (!isIp(host)) {
      const q = hostMediaUp(this.net, this.d) ? dnsQuery(this.net, this.id, host) : { ok: false };
      if (!q.ok) return { dns: false };
      ip = q.address;
    }
    const rt = roundTrip(this.net, this.id, ip, { proto: 'tcp', port });
    if (rt.ok) {
      this.d.connections.push({ ip, port, lport: 50000 + (this.d.connections.length * 37) % 9000, state: 'TIME_WAIT', pid: 0 });
      if (this.d.connections.length > 10) this.d.connections.shift();
    }
    return { dns: true, ip, ok: rt.ok, refused: rt.stage === 'refused', target: rt.target };
  }
  parseUrl(u) {
    const m = String(u || '').match(/^(?:(https?):\/\/)?([^/:]+)(?::(\d+))?/i);
    if (!m) return null;
    const scheme = (m[1] || 'http').toLowerCase();
    return { scheme, host: m[2], port: m[3] ? +m[3] : scheme === 'https' ? 443 : 80 };
  }
  psError(cmd, msg, cat) {
    return [
      { text: `${cmd} : ${msg}`, cls: 'err' }, { text: 'At line:1 char:1', cls: 'err' }, { text: `+ ${this.state.history.at(-1)}`, cls: 'err' },
      { text: `+ ${'~'.repeat(Math.min(60, this.state.history.at(-1).length))}`, cls: 'err' },
      { text: `    + CategoryInfo          : ${cat}`, cls: 'err' }, { text: '    + FullyQualifiedErrorId : WebCmdletWebResponseException,Microsoft.PowerShell.Commands.InvokeWebRequestCommand', cls: 'err' }, '',
    ];
  }
  iwr(args) {
    const u = this.parseUrl(args.find((a) => !a.startsWith('-')));
    if (!u) return { key: 'curl', ok: false, lines: this.psError('curl', 'Cannot bind parameter \'Uri\'.', 'InvalidArgument') };
    const c = this.connect(u.host, u.port);
    const args2 = { url: `${u.scheme}://${u.host}`, host: u.host, port: u.port, ip: c.ip };
    if (!c.dns) return { key: 'curl', args: args2, result: { success: false, dns: false }, lines: [{ text: '', delay: 2000 }, ...this.psError('curl', `The remote name could not be resolved: '${u.host}'`, 'InvalidOperation: (System.Net.HttpWebRequest:HttpWebRequest) [Invoke-WebRequest], WebException')] };
    if (!c.ok) return { key: 'curl', args: args2, result: { success: false, refused: c.refused }, lines: [{ text: '', delay: c.refused ? 300 : 4000 }, ...this.psError('curl', 'Unable to connect to the remote server', 'InvalidOperation: (System.Net.HttpWebRequest:HttpWebRequest) [Invoke-WebRequest], WebException')] };
    const page = c.target.http || { title: 'IIS Windows Server', body: 'Welcome' };
    const html = `<!doctype html><html><head><title>${page.title}</title>...`;
    return {
      key: 'curl', args: args2, result: { success: true, status: 200 },
      lines: ['', 'StatusCode        : 200', 'StatusDescription : OK', `Content           : ${html}`, 'RawContent        : HTTP/1.1 200 OK',
        '                    Content-Type: text/html; charset=UTF-8', `                    Server: ${page.server || 'ECS (sec/96EE)'}`,
        '                    Content-Length: 1256', '', 'Forms             : {}', 'Headers           : {[Content-Type, text/html; charset=UTF-8], [Content-Length, 1256]}',
        'Images            : {}', 'InputFields       : {}', 'Links             : {@{innerHTML=More information...}}', 'ParsedHtml        : mshtml.HTMLDocumentClass', 'RawContentLength  : 1256', ''],
    };
  }
  curlExe(args) {
    const u = this.parseUrl(args.find((a) => !a.startsWith('-')));
    if (!u) return { key: 'curl', ok: false, lines: ['curl: try \'curl --help\' for more information'] };
    const c = this.connect(u.host, u.port);
    const args2 = { url: `${u.scheme}://${u.host}`, host: u.host, port: u.port, ip: c.ip };
    if (!c.dns) return { key: 'curl', args: args2, result: { success: false }, lines: [{ text: `curl: (6) Could not resolve host: ${u.host}`, delay: 2000 }] };
    if (!c.ok) return { key: 'curl', args: args2, result: { success: false, refused: c.refused }, lines: [{ text: c.refused ? `curl: (7) Failed to connect to ${u.host} port ${u.port} after 3 ms: Connection refused` : `curl: (28) Failed to connect to ${u.host} port ${u.port} after 21045 ms: Timed out`, delay: c.refused ? 200 : 4000 }] };
    const page = c.target.http || { title: 'IIS Windows Server' };
    const head = ['HTTP/1.1 200 OK', 'Content-Type: text/html; charset=UTF-8', `Server: ${page.server || 'ECS (sec/96EE)'}`, 'Content-Length: 1256', ''];
    return { key: 'curl', args: args2, result: { success: true, status: 200 }, lines: args.some((a) => a.toLowerCase() === '-i' || a === '--head') ? head : [`<!doctype html><html><head><title>${page.title}</title></head><body>${page.body || ''}</body></html>`] };
  }
  // ------------------------------------------------ Windows services (lab servers: the AAA service)
  svcList() {
    const a = this.d.aaa;
    const t = this.d.tftp ? [{ name: 'RzTftp', display: 'RouteZero TFTP Server (UDP 69)', obj: this.d.tftp }] : [];
    if (!a) return t;
    return [...t,
      { name: 'RzAaaRadius', display: 'RouteZero AAA - RADIUS (UDP 1812/1813)', obj: a.radius },
      { name: 'RzAaaTacacs', display: 'RouteZero AAA - TACACS+ (TCP 49)', obj: a.tacacs },
    ];
  }
  services(verb, args) {
    const list = this.svcList();
    const names = args.filter((x) => !x.startsWith('-')).map((x) => x.replace(/[,'"]/g, ''));
    const re = (pat) => new RegExp(`^${pat.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')}$`, 'i');
    const sel = names.length ? list.filter((x) => names.some((n) => re(n).test(x.name))) : list;
    const key = { get: 'Get-Service', start: 'Start-Service', stop: 'Stop-Service', restart: 'Restart-Service' }[verb];
    if (!sel.length) {
      const n = names[0] || '';
      return { key, ok: false, args: { name: n }, lines: [{ text: `${key} : Cannot find any service with service name '${n}'.`, cls: 'err' }, { text: `    + CategoryInfo          : ObjectNotFound: (${n}:String) [${key}], ServiceCommandException`, cls: 'err' }, ''] };
    }
    if (verb !== 'get') for (const x of sel) x.obj.enabled = verb !== 'stop';
    const rows = verb === 'get' ? ['', 'Status   Name               DisplayName', '------   ----               -----------', ...sel.map((x) => `${(x.obj.enabled === false ? 'Stopped' : 'Running').padEnd(9)}${x.name.padEnd(19)}${x.display}`), ''] : [];
    if (verb === 'restart' || verb === 'start') rows.push(...sel.map((x) => ({ text: `WARNING: Waiting for service '${x.display} (${x.name})' to start...`, cls: 'warn', delay: 600 })));
    return { key, args: { names: sel.map((x) => x.name) }, result: { running: sel.filter((x) => x.obj.enabled !== false).map((x) => x.name) }, lines: rows };
  }

  // ------------------------------------------------ files on a lab TFTP server (C:\TFTP-Root)
  tftpPathOf(args) {
    const p = args.find((x) => !x.startsWith('-') && !/^(md5|sha256)$/i.test(x)) || '';
    const t = this.d.tftp;
    const norm = p.replace(/^\.\\/, '').replace(/[/]/g, '\\');
    if (!t) return { p, err: true };
    const root = t.root;
    if (!p || norm.toLowerCase() === root.toLowerCase() || norm.toLowerCase() === `${root.toLowerCase()}\\`) return { p: root, dir: true };
    const name = norm.toLowerCase().startsWith(`${root.toLowerCase()}\\`) ? norm.slice(root.length + 1) : norm;
    return { p: `${root}\\${name}`, name, file: t.files[name] || null };
  }
  notFound(key, p) {
    return { key, ok: false, args: { path: p }, lines: [{ text: `${key} : Cannot find path '${p}' because it does not exist.`, cls: 'err' }, { text: `    + CategoryInfo          : ObjectNotFound: (${p}:String) [${key}], ItemNotFoundException`, cls: 'err' }, ''] };
  }
  gci(args) {
    const r = this.tftpPathOf(args);
    if (r.err || !r.dir) return this.notFound('Get-ChildItem', r.p || `C:\\Users\\${this.d.user}`);
    const files = Object.values(this.d.tftp.files);
    return { key: 'Get-ChildItem', args: { path: r.p }, result: { files: files.map((f) => f.name) }, lines: ['', `    Directory: ${r.p}`, '', '', 'Mode                 LastWriteTime         Length Name', '----                 -------------         ------ ----',
      ...files.map((f) => `-a----        14/10/2026     08:0${files.indexOf(f) % 10}  ${String(f.size).padStart(14)} ${f.name}`), ''] };
  }
  fileHash(args) {
    const r = this.tftpPathOf(args);
    if (r.err || !r.file) return this.notFound('Get-FileHash', r.p);
    const alg = (args[args.findIndex((x) => /^-algorithm$/i.test(x)) + 1] || 'SHA256').toUpperCase();
    const hash = alg === 'MD5' ? r.file.md5.toUpperCase() : `${r.file.md5}${r.file.md5}`.toUpperCase();
    return { key: 'Get-FileHash', args: { path: r.p, name: r.name, alg }, result: { hash: hash.toLowerCase() }, lines: ['', 'Algorithm       Hash                                                                   Path', '---------       ----                                                                   ----', `${alg.padEnd(16)}${hash.padEnd(71)}${r.p}`, ''] };
  }
  gc(args) {
    const r = this.tftpPathOf(args);
    if (r.err || !r.file) return this.notFound('Get-Content', r.p);
    if (r.file.kind !== 'config') return { key: 'Get-Content', args: { path: r.p, name: r.name }, lines: ['[Lab] Binary file (IOS image) — contents not displayed.'] };
    return { key: 'Get-Content', args: { path: r.p, name: r.name }, result: { lines: r.file.lines.length }, lines: r.file.lines.flatMap((l) => String(l).split('\n')) };
  }

  // ------------------------------------------------ remote management (SSH / Telnet)
  resolveTarget(host) {
    if (isIp(host)) return { ip: host };
    const q = hostMediaUp(this.net, this.d) ? dnsQuery(this.net, this.id, host) : { ok: false };
    return q.ok ? { ip: q.address } : { ip: null };
  }
  ssh(args) {
    let user = null, host = null;
    for (let i = 0; i < args.length; i++) {
      const a = args[i];
      if (a === '-l') user = args[++i];
      else if (a === '-p') i++;
      else if (!a.startsWith('-')) host = a;
    }
    if (host && host.includes('@')) [user, host] = host.split('@');
    if (!host) return { key: 'ssh', ok: false, lines: ['usage: ssh [-46AaCfGgKkMNnqsTtVvXxYy] [-B bind_interface]', '           [-b bind_address] [-c cipher_spec] [-D [bind_address:]port]', '           [-l login_name] [-p port] destination [command]'] };
    user = user || this.d.user;
    const t = this.resolveTarget(host);
    if (!t.ip) return { key: 'ssh', ok: false, args: { host, user }, result: { success: false, stage: 'dns' }, lines: [{ text: `ssh: Could not resolve hostname ${host}: No such host is known.`, delay: 1500 }] };
    const c = remoteConnect(this.net, this.id, t.ip, 'ssh');
    const fail = (stage, text, delay = 300) => ({ key: 'ssh', ok: false, args: { host, ip: t.ip, user }, result: { success: false, stage }, lines: [{ text, delay }] });
    if (c.stage === 'timeout' || c.stage === 'unreachable') return fail('timeout', `ssh: connect to host ${t.ip} port 22: Connection timed out`, 3000);
    if (c.stage === 'refused') return fail('refused', `ssh: connect to host ${t.ip} port 22: Connection refused`);
    if (c.stage === 'reset') return fail('reset', 'kex_exchange_identification: read: Connection reset');
    if (c.stage === 'version') return fail('version', 'Protocol major versions differ: 2 vs. 1');
    this.state.remote = { proto: 'ssh', id: c.id, ip: t.ip, user, src: c.src, ask: 'pass', tries: 0, host };
    return { key: 'ssh connect', args: { host, ip: t.ip, user }, result: { stage: 'password' }, lines: [] };
  }
  telnet(args) {
    const host = args.find((a) => !a.startsWith('-'));
    if (!host) return { key: 'telnet', ok: false, lines: ['', 'telnet [-a][-e escape char][-f log file][-l user][-t term][host [port]]'] };
    const t = this.resolveTarget(host);
    const fail = (stage, extra = []) => ({ key: 'telnet', ok: false, args: { host, ip: t.ip }, result: { success: false, stage }, lines: [`Connecting To ${host}...`, ...extra] });
    if (!t.ip) return fail('dns', [{ text: 'Could not open connection to the host, on port 23: Connect failed', delay: 1500 }]);
    const c = remoteConnect(this.net, this.id, t.ip, 'telnet');
    if (c.stage !== 'ok') return fail(c.stage === 'reset' ? 'refused' : c.stage, [{ text: 'Could not open connection to the host, on port 23: Connect failed', delay: c.stage === 'refused' ? 400 : 3000 }]);
    const head = [`Connecting To ${host}...`, ...(c.banner != null ? c.banner.split('\n') : []), '', 'User Access Verification', ''];
    if (c.noPassword) return { key: 'telnet', ok: false, args: { host, ip: t.ip }, result: { success: false, stage: 'no-password' }, lines: [...head, 'Password required, but none set', '', 'Connection to host lost.'] };
    if (c.prompt === 'none') {
      const r = remoteLogin(this.net, c.id, { user: null, pass: null, src: c.src, proto: 'telnet' });
      return { key: 'telnet', args: { host, ip: t.ip }, result: { success: true, stage: 'ok', device: c.id, priv: r.priv, noLogin: true }, lines: [...head, ...this.sessionLines('telnet', r, t.ip)] };
    }
    this.state.remote = { proto: 'telnet', id: c.id, ip: t.ip, user: null, src: c.src, ask: c.prompt === 'username' ? 'user' : 'pass', tries: 0, host };
    return { key: 'telnet connect', args: { host, ip: t.ip }, result: { stage: c.prompt }, lines: head };
  }
  sessionLines(proto, r, ip) {
    const d = dev(this.net, r.id || this.state.remote?.id);
    return [
      `${r.hostname}${r.priv >= 15 ? '#' : '>'}`,
      { text: `[Lab] ${proto === 'ssh' ? 'SSH' : 'Telnet'} login to ${r.hostname} verified (privilege level ${r.priv >= 15 ? 15 : r.priv}). Remote commands are not simulated — use the ${d?.hostname || r.hostname} console tab.`, cls: 'dim' },
      proto === 'ssh' ? `Connection to ${ip} closed.` : '', ...(proto === 'ssh' ? [] : ['Connection to host lost.']),
    ];
  }
  remoteAnswer(raw, promptBefore) {
    const R = this.state.remote;
    const ev = (o) => ({ ...o, promptBefore, line: '' });
    if (R.ask === 'user') {
      R.user = raw.trim();
      R.ask = 'pass';
      return this.out([], ev({ key: `${R.proto} username`, args: { ip: R.ip, user: R.user } }));
    }
    const r = remoteLogin(this.net, R.id, { user: R.user, pass: raw, src: R.src, proto: R.proto });
    const base = { key: R.proto, args: { host: R.host, ip: R.ip, user: R.user } };
    if (r.ok) {
      this.state.remote = null;
      const mot = R.proto === 'ssh' && dev(this.net, R.id).mgmt?.banner != null ? dev(this.net, R.id).mgmt.banner.split('\n') : [];
      return this.out([...mot, '', ...this.sessionLines(R.proto, { ...r, id: R.id }, R.ip)], ev({ ...base, result: { success: true, stage: 'ok', device: R.id, priv: r.priv, method: r.method } }));
    }
    R.tries++;
    if (R.tries >= 3) {
      this.state.remote = null;
      const lines = R.proto === 'ssh' ? [`${R.user}@${R.ip}: Permission denied (password).`] : [r.method === 'line' ? '% Bad passwords' : '% Authentication failed', '', 'Connection to host lost.'];
      return this.out(lines, ev({ ...base, ok: false, result: { success: false, stage: 'auth', method: r.method } }));
    }
    if (R.proto === 'ssh') return this.out(['Permission denied, please try again.'], ev({ ...base, ok: false, result: { success: false, stage: 'retry' } }));
    if (r.method === 'line' || !R.user) return this.out([], ev({ ...base, ok: false, result: { success: false, stage: 'retry' } }));
    R.ask = 'user';
    return this.out(['% Login invalid', ''], ev({ ...base, ok: false, result: { success: false, stage: 'retry' } }));
  }

  tnc(args) {
    let host = null, port = null;
    for (let i = 0; i < args.length; i++) {
      const a = args[i].toLowerCase();
      if (a === '-port' || a === '-p') port = +args[++i];
      else if (a === '-computername' || a === '-cn') host = args[++i];
      else if (a === '-commontcpport') { const p = { http: 80, rdp: 3389, smb: 445, winrm: 5985 }[String(args[++i]).toLowerCase()]; port = p || null; }
      else if (!a.startsWith('-')) host = args[i];
    }
    host = host || 'internetbeacon.msedge.net';
    const src = hostIp(this.d)?.ip || '0.0.0.0';
    let ip = host;
    if (!isIp(host)) {
      const q = hostMediaUp(this.net, this.d) ? dnsQuery(this.net, this.id, host) : { ok: false };
      if (!q.ok) return { key: 'Test-NetConnection', args: { host, port }, result: { success: false }, lines: [{ text: `WARNING: Name resolution of ${host} failed`, cls: 'warn', delay: 2000 }, '', `ComputerName   : ${host}`, 'RemoteAddress  :', 'InterfaceAlias :', 'SourceAddress  :', 'PingSucceeded  : False', ''] };
      ip = q.address;
    }
    if (port) {
      const c = this.connect(ip, port);
      const out = [];
      if (!c.ok) out.push({ text: `WARNING: TCP connect to (${ip} : ${port}) failed`, cls: 'warn', delay: c.refused ? 500 : 4000 });
      out.push('', `ComputerName     : ${host}`, `RemoteAddress    : ${ip}`, `RemotePort       : ${port}`, 'InterfaceAlias   : Ethernet', `SourceAddress    : ${src}`, `TcpTestSucceeded : ${c.ok ? 'True' : 'False'}`, '');
      return { key: 'Test-NetConnection', args: { host, ip, port }, result: { success: c.ok, refused: c.refused }, lines: out };
    }
    const rt = roundTrip(this.net, this.id, ip, { ttl: 128 });
    const out = [];
    if (!rt.ok) out.push({ text: `WARNING: Ping to ${ip} failed with status: TimedOut`, cls: 'warn', delay: 3000 });
    out.push('', `ComputerName           : ${host}`, `RemoteAddress          : ${ip}`, 'InterfaceAlias         : Ethernet', `SourceAddress          : ${src}`, `PingSucceeded          : ${rt.ok ? 'True' : 'False'}`, ...(rt.ok ? [`PingReplyDetails (RTT) : ${rt.hops <= 1 ? 0 : rt.hops * 4} ms`] : []), '');
    return { key: 'Test-NetConnection', args: { host, ip }, result: { success: rt.ok }, lines: out };
  }
}
export { linkOf };
